import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { catchError, combineLatest, map, Observable } from 'rxjs';
import { LocalStorage } from '../local-storage/local-storage';
import { UserService } from '../auth/user.service';
import { NetworkProvider } from '../network/network';
import { AgendaOtService } from './agenda-ot.service';
import { AgendaTempoService } from './agenda-tempo.service';
import { AgendaActiviteInterneService } from './agenda-activite-interne.service';
import { AgendaCrServicePartiel } from './agenda-cr-service-partiel.service';
import { ByDayCollection } from '../../models/collections.model';
import {
  AgendaJour,
  AgendaJourActiviteInterne,
  AgendaJourCrPartiel,
  AgendaJourOt,
  AgendaJourTempo,
} from '../../models/agenda/agenda-jour.model';
import { handleHttpError } from '../../utils/httpUtils';
import { AgendaEquipeRequest, SemaineEquipe } from '../../models/agenda/semaine-equipe.model';
import { environment } from '../../../../environments/environment';
import { EquipesSecteur } from '../../models/agenda/equipes-secteur';
import { MotifIndisponibilite } from '../../models/agenda/indisponibilite.model';
import { OT } from '../../models/agenda/ot.model';
import { Activite } from '../../models/agenda/activite.model';

@Injectable({ providedIn: 'root' })
export class AgendaService extends LocalStorage {
  constructor(
    private userService: UserService,
    private http: HttpClient,
    protected override network: NetworkProvider,
    private agendaOtService: AgendaOtService,
    private agendaTempoService: AgendaTempoService,
    private agendaActiviteInterneService: AgendaActiviteInterneService,
    private agendaCrService: AgendaCrServicePartiel
  ) {
    super();
  }

  /**
   * Merge le retour tempo, les ots et les activités internes
   *
   * @param displayedDate
   * @param forceRefresh
   */
  getAgenda(displayedDate: Date, forceRefresh = false): Observable<ByDayCollection<AgendaJour>> {
    const byDayCollectionTempo: Observable<ByDayCollection<AgendaJourTempo>> = this.agendaTempoService.getAgendaTempo(
      displayedDate,
      forceRefresh
    );

    const byDayCollectionOt: Observable<ByDayCollection<AgendaJourOt>> = this.agendaOtService.getAgendaOt(
      displayedDate,
      forceRefresh
    );

    const byDayCollectionActiviteInterne: Observable<ByDayCollection<AgendaJourActiviteInterne>> =
      this.agendaActiviteInterneService.getAgendaActiviteInterne(displayedDate);

    const byDayCollectionCrPartiel: Observable<ByDayCollection<AgendaJourCrPartiel>> =
      this.agendaCrService.getAgendaCrPartiel(displayedDate);

    return combineLatest([
      byDayCollectionTempo,
      byDayCollectionOt,
      byDayCollectionActiviteInterne,
      byDayCollectionCrPartiel,
    ]).pipe(
      map(([collectionTempo, collectionOT, collectionActiviteInterne, collectionCrPartiel]) => {
        let collection: ByDayCollection<AgendaJour> = new ByDayCollection();

        collectionTempo.dates().forEach((date) => {
          collection.put(date, {
            activitesInternes: [],
            ots: [],
            crs: [],
            ...collectionTempo.get(date),
          });
        });

        collectionOT.dates().forEach((date) => {
          if (collection.get(date)) {
            let day = collection.get(date);
            day.ots = this.maskOtInAbsences(collectionOT.get(date).ots, day.absences);
            collection.put(date, day);
          } else {
            collection.put(date, {
              activitesInternes: [],
              absences: [],
              astreintes: [],
              indispos: [],
              plageActivites: [],
              crs: [],
              ...collectionOT.get(date),
            });
          }
        });

        collectionActiviteInterne.dates().forEach((date) => {
          if (collection.get(date)) {
            let day = collection.get(date);
            day.activitesInternes = collectionActiviteInterne.get(date).activitesInternes;
          } else {
            collection.put(date, {
              absences: [],
              astreintes: [],
              indispos: [],
              ots: [],
              plageActivites: [],
              crs: [],
              ...collectionActiviteInterne.get(date),
            });
          }
        });

        collectionCrPartiel.dates().forEach((date) => {
          if (collection.get(date)) {
            let day = collection.get(date);
            day.crs = collectionCrPartiel.get(date).crs;
          } else {
            collection.put(date, {
              absences: [],
              astreintes: [],
              indispos: [],
              ots: [],
              plageActivites: [],
              activitesInternes: [],
              ...collectionCrPartiel.get(date),
            });
          }
        });

        collection.dates().forEach((date) => {
          collection.put(date, this.convertAgendaJourStringToDates(collection.get(date)));
        });
        return collection;
      }),
      catchError(handleHttpError)
    );
  }

  /**
   * Retourne les activités des semaines des techniciens dans l'équipe de l'utilisateur entre deux dates
   * @param idEquipe Id de l'equipe
   * @param dateDebut Date de début
   * @param dateFin Date de fin
   */
  getAgendaEquipeV2(idEquipe: string, dateDebut: Date, dateFin: Date): Observable<SemaineEquipe[]> {
    const dateIsoDebut = moment(dateDebut).toISOString();
    const dateIsoFin = moment(dateFin).toISOString();
    let request: AgendaEquipeRequest = {
      dateDebut: dateIsoDebut,
      dateFin: dateIsoFin,
      idEquipe: idEquipe,
    };
    return this.http
      .post<SemaineEquipe[]>(environment.apis.API_AGENDA_EQUIPE_V2, request)
      .pipe(catchError(handleHttpError));
  }
  /**
   * Retourne la liste des equipes du secteur de l'utilisateur avec leurs identifiants et libellé
   */
  getListeEquipeSecteur(): Observable<EquipesSecteur[]> {
    let url = `${environment.apis.API_DACTECH_BACK_ROOT}/v1/equipesSecteur/${this.userService.getDkCodeRessource()}`;
    return this.http.get<EquipesSecteur[]>(url).pipe(catchError(handleHttpError));
  }

  /** Enregistre  une indispo immediate dans l'agenda du technicien
   * @param dateDebut Date de début
   * @param dateFin Date de fin
   * @param motif Motif
   * @param commentaire Commentaire
   */
  addIndispoImmediate(
    dateDebut: Date,
    dateFin: Date,
    motif: MotifIndisponibilite,
    commentaire: string
  ): Observable<boolean> {
    const dateIsoDebut = moment(dateDebut).toISOString();
    const dateIsoFin = moment(dateFin).toISOString();

    return this.http.post<boolean>(`${environment.apis.API_DACTECH_BACK_V1}/indispo`, {
      dkCodeRessource: this.userService.getDkCodeRessource(),
      dateDebut: dateIsoDebut,
      dateFin: dateIsoFin,
      motif: motif.toString(),
      commentaire: commentaire,
    });
  }

  convertAgendaJourStringToDates(jour: AgendaJour): AgendaJour {
    return {
      activitesInternes: jour.activitesInternes.map((activiteInterne) => ({
        ...activiteInterne,
        dateDebut: new Date(activiteInterne.dateDebut),
        dateFin: new Date(activiteInterne.dateFin),
      })),
      indispos: jour.indispos.map((indispo) => ({
        ...indispo,
        dateDebut: new Date(indispo.dateDebut),
        dateFin: new Date(indispo.dateFin),
      })),
      astreintes: jour.astreintes.map((astreinte) => ({
        ...astreinte,
        dateDebut: new Date(astreinte.dateDebut),
        dateFin: new Date(astreinte.dateFin),
      })),
      absences: jour.absences.map((absence) => ({
        ...absence,
        dateDebut: new Date(absence.dateDebut),
        dateFin: new Date(absence.dateFin),
      })),
      ots: jour.ots.map((ot) => ({
        ...ot,
        dateDebut: new Date(ot.dateDebut),
        dateFin: new Date(ot.dateFin),
      })),
      plageActivites: jour.plageActivites.map((plageActivite) => ({
        ...plageActivite,
        dateDebut: new Date(plageActivite.dateDebut),
        dateFin: new Date(plageActivite.dateFin),
      })),
      crs: jour.crs.map((cr) => ({
        ...cr,
        dateDebut: new Date(cr.intervention_date_debut),
        dateFin: new Date(cr.intervention_date_fin),
      })),
    };
  }

  /**
   * @param ots Tableau d'ot
   * @param absences Tableau d'absences
   * @returns Les OTs découpées en fonction des absences en parallèles
   */
  maskOtInAbsences(ots: OT[], absences: Activite[]): OT[] {
    const newOts: OT[] = [];
    ots.forEach((ot) => {
      const ovAbs = this.getOverlappedAbsences(ot, absences);
      if (!ovAbs.length) {
        newOts.push(ot);
      } else {
        newOts.push({ ...ot, dateFin: new Date(ovAbs[0].dateDebut) });

        for (let i = 1; i < ovAbs.length; i++) {
          newOts.push({
            ...ot,
            dateDebut: new Date(ovAbs[i - 1].dateFin),
            dateFin: new Date(ovAbs[i].dateDebut),
          });
        }
        newOts.push({
          ...ot,
          dateDebut: new Date(ovAbs[ovAbs.length - 1].dateFin),
        });
      }
    });
    return newOts.filter((ot) => !moment(ot.dateDebut).isSame(ot.dateFin, 'minute'));
  }

  /**
   * @param ot Le ot
   * @param absences tableau d'absences
   * @returns les absences chevauchées entièrement par un OT
   */
  getOverlappedAbsences(ot: OT, absences: Activite[]): Activite[] {
    return absences
      .filter(
        (absence) =>
          moment(absence.dateDebut).isSameOrAfter(ot.dateDebut) && moment(absence.dateFin).isSameOrBefore(ot.dateFin)
      )
      .sort((a1, a2) => moment(a1.dateDebut).diff(a2.dateDebut));
  }
}
